import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";

export type DeviceContext = Record<string, string | number | boolean>;

export async function collectDeviceContext(): Promise<DeviceContext> {
  const context: DeviceContext = {
    platform: Capacitor.getPlatform(),
    release: process.env.REACT_APP_REPOSITORY_DATE || "unknown",
    url: window.location.href,
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    pixelRatio: window.devicePixelRatio,
    reportedAt: new Date().toISOString()
  };

  try {
    const info = await Device.getInfo();
    context.model = info.model;
    context.manufacturer = info.manufacturer;
    context.operatingSystem = info.operatingSystem;
    context.osVersion = info.osVersion;
    context.isVirtual = info.isVirtual;
    if (info.webViewVersion) {
      context.webViewVersion = info.webViewVersion;
    }
    if (typeof info.memUsed === "number") {
      context.memUsedMb = Math.round(info.memUsed / (1024 * 1024));
    }
  } catch {
    /* Device plugin unavailable on this platform */
  }

  try {
    const battery = await Device.getBatteryInfo();
    if (typeof battery.batteryLevel === "number") {
      context.batteryLevel = `${Math.round(battery.batteryLevel * 100)}%`;
    }
    if (typeof battery.isCharging === "boolean") {
      context.isCharging = battery.isCharging;
    }
  } catch {
    /* Battery info unavailable on this platform */
  }

  if (Capacitor.isNativePlatform()) {
    try {
      const appInfo = await App.getInfo();
      context.appVersion = appInfo.version;
      context.appBuild = appInfo.build;
    } catch {
      /* App info unavailable */
    }
  }

  return context;
}

export function formatDeviceContext(context: DeviceContext): string {
  const lines = Object.entries(context).map(([key, value]) => `${key}: ${value}`);
  return `--- Device info ---\n${lines.join("\n")}`;
}
